import clsx from 'clsx'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import backIcon from './assets/icon-back.svg'
import eyeIcon from './assets/icon-eye.svg'
import eyeSlashIcon from './assets/icon-eye-slash.svg'
import { useChangePassword } from './hooks/useChangePassword'

const SAME_PASSWORD_ERROR_MESSAGE =
  'New password cannot be the same as the current password'

/**
 * ChangePassword 页面
 * 用户修改密码页面
 */
export default function ChangePassword() {
  const navigate = useNavigate()
  const {
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    isLoading,
    errorMessage,
    successMessage,
    handleSave,
    canSave,
    validationRequirements,
  } = useChangePassword()

  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const isSamePasswordError =
    (currentPassword.length > 0 &&
      newPassword.length > 0 &&
      currentPassword === newPassword) ||
    errorMessage === SAME_PASSWORD_ERROR_MESSAGE
  const currentPasswordErrorMessage = isSamePasswordError ? '' : errorMessage
  const newPasswordErrorMessage = isSamePasswordError
    ? SAME_PASSWORD_ERROR_MESSAGE
    : ''

  return (
    <div
      className='bg-white px-[20px] pt-[20px] font-sans'
      data-testid='change-password-page'
    >
      {/* 顶部导航栏 */}
      <header className='flex items-center h-[44px] mb-[20px]'>
        <button
          type='button'
          onClick={() => navigate(-1)}
          className='flex items-center justify-center size-[32px] rounded-full bg-[#f6f6f6] active:bg-black/5 transition-colors'
          data-testid='back-button'
        >
          <img src={backIcon} alt='Back' className='size-[24px]' />
        </button>
        <h1 className='flex-1 text-center font-black text-[18px] text-black pr-[32px]'>
          Change Password
        </h1>
      </header>

      {/* 表单区域 */}
      <main className='flex flex-col gap-[24px]'>
        {/* 当前密码 */}
        <div className='flex flex-col gap-[8px]'>
          <label className='font-medium text-[12px] text-black'>
            Current Password
          </label>
          <div
            className={clsx(
              'relative flex items-center h-[48px] bg-[#f6f6f6] rounded-[12px] px-[16px] border transition-all',
              currentPasswordErrorMessage
                ? 'border-[#FF3B30] bg-[#FFF5F5]'
                : 'border-transparent',
            )}
          >
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder='Please enter'
              className='flex-1 bg-transparent border-none outline-none text-[14px] text-black placeholder:text-black/30'
              data-testid='current-password-input'
            />
            <button
              type='button'
              onClick={() => setShowCurrent(!showCurrent)}
              className='ml-[10px] opacity-60 active:opacity-100 transition-opacity'
            >
              <img
                src={showCurrent ? eyeIcon : eyeSlashIcon}
                alt=''
                className='size-[20px]'
              />
            </button>
          </div>
          {currentPasswordErrorMessage && (
            <p
              className='text-[12px] text-[#FF3B30] leading-normal m-0 animate-in fade-in slide-in-from-top-1'
              data-testid='error-message'
            >
              {currentPasswordErrorMessage}
            </p>
          )}
        </div>

        {/* 新密码 */}
        <div className='flex flex-col gap-[8px]'>
          <label className='font-medium text-[12px] text-black'>
            New password
          </label>
          <div
            className={clsx(
              'relative flex items-center h-[48px] bg-[#f6f6f6] rounded-[12px] px-[16px] border',
              newPasswordErrorMessage
                ? 'border-[#FF3B30] bg-[#FFF5F5]'
                : 'border-transparent',
            )}
          >
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder='Please enter'
              className='flex-1 bg-transparent border-none outline-none text-[14px] text-black placeholder:text-black/30'
              data-testid='new-password-input'
            />
            <button
              type='button'
              onClick={() => setShowNew(!showNew)}
              className='ml-[10px] opacity-60 active:opacity-100 transition-opacity'
            >
              <img
                src={showNew ? eyeIcon : eyeSlashIcon}
                alt=''
                className='size-[20px]'
              />
            </button>
          </div>
          {newPasswordErrorMessage && (
            <p
              className='text-[12px] text-[#FF3B30] leading-normal m-0 animate-in fade-in slide-in-from-top-1'
              data-testid='new-password-error-message'
            >
              {newPasswordErrorMessage}
            </p>
          )}
        </div>

        {/* 确认新密码 */}
        <div className='flex flex-col gap-[8px]'>
          <label className='font-medium text-[12px] text-black'>
            Confirm new password
          </label>
          <div className='relative flex items-center h-[48px] bg-[#f6f6f6] rounded-[12px] px-[16px] border border-transparent'>
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder='Please enter'
              className='flex-1 bg-transparent border-none outline-none text-[14px] text-black placeholder:text-black/30'
              data-testid='confirm-password-input'
            />
            <button
              type='button'
              onClick={() => setShowConfirm(!showConfirm)}
              className='ml-[10px] opacity-60 active:opacity-100 transition-opacity'
            >
              <img
                src={showConfirm ? eyeIcon : eyeSlashIcon}
                alt=''
                className='size-[20px]'
              />
            </button>
          </div>
        </div>

        {/* 验证要求列表 */}
        <section className='flex flex-col gap-[8px] mt-[-12px]'>
          <ValidationItem
            met={validationRequirements.length}
            text='8-16 characters, no spaces'
          />
          <ValidationItem
            met={validationRequirements.uppercase}
            text='At least 1 uppercase letter'
          />
          <ValidationItem
            met={validationRequirements.lowercase}
            text='At least 1 lowercase letter'
          />
          <ValidationItem
            met={validationRequirements.number}
            text='At least 1 number'
          />
          <ValidationItem
            met={validationRequirements.special}
            text='At least 1 special character'
          />
          <ValidationItem
            met={validationRequirements.match}
            text='Two passwords must be consistent'
          />
        </section>

        {/* 保存按钮 */}
        <button
          type='button'
          disabled={!canSave || isLoading}
          onClick={handleSave}
          className={clsx(
            'flex items-center justify-center h-[48px] rounded-[12px] mt-[12px] font-bold text-[14px] text-white transition-all',
            canSave && !isLoading
              ? 'bg-[#6e59cd] active:scale-[0.98] shadow-[0px_8px_16px_rgba(110,89,205,0.3)]'
              : 'bg-[#6e59cd] opacity-30 cursor-not-allowed',
          )}
          data-testid='save-button'
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </main>

      {/* 成功提示 */}
      {successMessage && (
        <div
          className='fixed top-[50%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-black/80 text-white px-[20px] py-[12px] rounded-[12px] text-center max-w-[80%] shadow-2xl animate-in fade-in zoom-in-95'
          data-testid='success-toast'
        >
          {successMessage}
        </div>
      )}
    </div>
  )
}

/**
 * 验证项组件
 */
function ValidationItem(props: { met: boolean; text: string }) {
  return (
    <div className='flex items-center gap-[6px] transition-colors'>
      <div
        className={clsx(
          'size-[14px] rounded-full flex items-center justify-center border transition-all',
          props.met
            ? 'bg-[#34C759] border-[#34C759]'
            : 'bg-transparent border-black/20',
        )}
      >
        <svg
          viewBox='0 0 10 10'
          fill='none'
          className={clsx(
            'size-[8px] transition-all',
            props.met ? 'opacity-100 scale-100' : 'opacity-0 scale-50',
          )}
        >
          <path
            d='M2 5L4 7L8 3'
            stroke='white'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
      <span
        className={clsx(
          'text-[12px] leading-tight transition-colors',
          props.met ? 'text-[#34C759]' : 'text-black/50',
        )}
      >
        {props.text}
      </span>
    </div>
  )
}
